import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Dropdown,
    Empty,
    List,
    message,
    Modal,
    Pagination,
    Row,
    Space,
    Spin,
    Table,
    Typography,
    Tooltip,
    Tag,
} from 'antd';
import type { MenuProps } from 'antd';
import {
    DownOutlined,
    EnvironmentOutlined,
    ExclamationCircleOutlined,
    FileImageOutlined,
    ImportOutlined,
    ReloadOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import AreaMap from '../components/Map/AreaMap';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/Measurements.css';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../utils/measurementTime';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface AreaType {
    id: number;
    name: string;
}

interface AreaRow {
    id: number | 'no-area';
    name: string;
    areaType?: AreaType | null;
    geom?: any;
    parentAreaId?: number | null;
    children?: AreaRow[];
    isSynthetic?: boolean;
    counts?: MeasurementCounts;
}

interface MeasurementCounts {
    total: number;
    assignedToPoint: number;
    inPatternNoPoint: number;
    noPattern: number;
}

interface PatternItem {
    id: number;
    name: string;
    patternType?: string;
}

interface PatternPointItem {
    id: number;
    label?: string | null;
    pointIndex: number;
    gpsLat?: number | null;
    gpsLon?: number | null;
}

interface MeasurementValue {
    valueType: string;
    numValue?: number | null;
    textValue?: string | null;
    processingStage?: string | null;
}

interface MeasurementFile {
    id: number;
    fileName: string;
    fileType?: string;
    fileSize?: number;
    mimeType?: string;
    signedUrl?: string;
    processingStage?: string | null;
}

interface MeasurementText {
    id: number;
    text: string;
    textType?: string;
    createdAt?: string;
    processingStage?: string | null;
}

interface MeasurementItem {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    areaId?: number | null;
    area?: { id: number; name: string } | null;
    patternId?: number | null;
    pattern?: PatternItem | null;
    patternPointId?: number | null;
    patternPoint?: PatternPointItem | null;
    device?: { id: number; label: string } | null;
    geom?: { type: string; coordinates: number[] } | null;
    files?: MeasurementFile[];
    values?: MeasurementValue[];
    texts?: MeasurementText[];
}

type MeasurementStatusKey = 'assigned' | 'in-pattern-no-point' | 'no-pattern' | 'no-area';

interface MeasurementTableRow extends MeasurementItem {
    statusKey: MeasurementStatusKey;
}

const Measurements: React.FC = () => {
    const navigate = useNavigate();
    const [areas, setAreas] = useState<AreaRow[]>([]);
    const [selectedAreaId, setSelectedAreaId] = useState<number | 'no-area' | null>(null);
    const [measurements, setMeasurements] = useState<MeasurementItem[]>([]);
    const [measurementsForMap, setMeasurementsForMap] = useState<MeasurementItem[]>([]); // Separate state for map markers
    const [totalMeasurements, setTotalMeasurements] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [patterns, setPatterns] = useState<PatternItem[]>([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [loadingMeasurements, setLoadingMeasurements] = useState(false);
    const [loadingPatterns, setLoadingPatterns] = useState(false);
    const [loadingAreaCounts, setLoadingAreaCounts] = useState(false);
    const [loadingMapPoints, setLoadingMapPoints] = useState(false);
    const getInitialDateRange = (): [Dayjs, Dayjs] => {
        try {
            const stored = localStorage.getItem('measurementDateRange');
            if (stored) {
                const { from, to } = JSON.parse(stored);
                return [dayjs(from), dayjs(to)];
            }
        } catch (error) {
            console.warn('Failed to load measurement date range', error);
        }
        return [dayjs().subtract(7, 'day'), dayjs()];
    };

    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getInitialDateRange());
    const latestUploadsHidden = dateRange[1].isBefore(dayjs(), 'day');
    const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementItem | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<MeasurementFile[]>([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [areaTypeTemplates, setAreaTypeTemplates] = useState<Map<number, string>>(new Map());
    const [checkedAreaTypes, setCheckedAreaTypes] = useState<Set<number>>(new Set());
    const [patternPointsByPattern, setPatternPointsByPattern] = useState<Record<number, PatternPointItem[]>>({});
    const filterProcessed = useCallback(
        <T extends { processingStage?: string | null }>(items?: T[]) =>
            (items || []).filter((item) => item.processingStage === 'processed'),
        []
    );

    useEffect(() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return;
        }
        localStorage.setItem('measurementDateRange', JSON.stringify({
            from: dateRange[0].format('YYYY-MM-DD'),
            to: dateRange[1].format('YYYY-MM-DD'),
        }));
    }, [dateRange]);

    const fetchAreas = useCallback(async () => {
        setLoadingAreas(true);
        try {
            const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true });
            const payload: AreaRow[] = res.data?.data || res.data || [];
            setAreas(payload);
        } catch (error) {
            console.error('Failed to load areas', error);
        } finally {
            setLoadingAreas(false);
        }
    }, []);

    // Fetch area counts using optimized server-side aggregation
    const fetchAreaCounts = useCallback(async () => {
        try {
            const params: Record<string, any> = {};
            
            // Apply date range filter
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dateRange[0].startOf('day').toISOString();
                params.to_date = dateRange[1].endOf('day').toISOString();
            }
            
            const res = await axios.get(ENDPOINTS.MEASUREMENTS_AREA_COUNTS, {
                params,
                withCredentials: true,
            });
            
            const counts: Array<{
                areaId: number | null;
                total: number;
                assignedToPoint: number;
                inPatternNoPoint: number;
                noPattern: number;
            }> = res.data?.data || [];
            
            console.log('[Measurements] Area counts loaded:', counts.length);
            return counts;
        } catch (error) {
            console.error('Failed to load area counts', error);
            return [];
        }
    }, [dateRange]);

    const refreshAreaCounts = useCallback(async () => {
        setLoadingAreaCounts(true);
        const counts = await fetchAreaCounts();
        const map = new Map<number | 'no-area', MeasurementCounts>();
        counts.forEach((c) => {
            const key = (c.areaId ?? 'no-area') as number | 'no-area';
            map.set(key, {
                total: c.total,
                assignedToPoint: c.assignedToPoint,
                inPatternNoPoint: c.inPatternNoPoint,
                noPattern: c.noPattern,
            });
        });
        setAreaCountsMap(map);
        setLoadingAreaCounts(false);
    }, [fetchAreaCounts]);

    // Fetch paginated measurements for display (filtered by selected area)
    const fetchMeasurements = useCallback(async (page: number = currentPage, size: number = pageSize, areaId: number | 'no-area' | null = selectedAreaId) => {
        setLoadingMeasurements(true);
        try {
            const offset = (page - 1) * size;
            const params: Record<string, any> = {
                limit: size,
                offset
            };
            
            // Apply area filter
            if (areaId && areaId !== 'no-area') {
                params.area_id = areaId;
            } else if (areaId === 'no-area') {
                params.unassigned = 'true';
            }
            
            // Apply date range filter
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dateRange[0].startOf('day').toISOString();
                params.to_date = dateRange[1].endOf('day').toISOString();
            }
            
            console.log('[Measurements] Fetching with params:', params);
            
            const res = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                params,
                withCredentials: true,
            });
            
            const data: MeasurementItem[] = res.data?.data || [];
            const total = res.data?.total || 0;
            
            setMeasurements(data);
            setTotalMeasurements(total);
            
            console.log('[Measurements] Received:', { count: data.length, total });
        } catch (error) {
            console.error('Failed to load measurements', error);
            setMeasurements([]);
            setTotalMeasurements(0);
        } finally {
            setLoadingMeasurements(false);
        }
    }, [currentPage, pageSize, dateRange, selectedAreaId]);

    // Fetch measurements specifically for map markers (higher limit for spatial context)
    const fetchMeasurementsForMap = useCallback(async (areaId: number | 'no-area' | null) => {
        if (!areaId) {
            setLoadingMapPoints(false);
            setMeasurementsForMap([]);
            return;
        }

        setLoadingMapPoints(true);
        try {
            // Use lower limit for unassigned measurements to avoid backend timeout
            const isUnassigned = areaId === 'no-area';
            const params: Record<string, any> = {
                limit: isUnassigned ? 500 : 2000  // Lower limit for unassigned to prevent backend overload
            };
            
            // Apply area filter
            if (!isUnassigned) {
                params.area_id = areaId;
            } else {
                params.unassigned = 'true';
            }
            
            // Apply date range filter
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dateRange[0].startOf('day').toISOString();
                params.to_date = dateRange[1].endOf('day').toISOString();
            }
            
            console.log('[Measurements] Fetching map markers with params:', params);
            
            const res = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                params,
                withCredentials: true,
            });
            
            const data: MeasurementItem[] = res.data?.data || [];
            setMeasurementsForMap(data);
            
            console.log('[Measurements] Map markers loaded:', data.length);
        } catch (error) {
            console.error('Failed to load measurements for map', error);
            // On error, use the paginated measurements as fallback
            setMeasurementsForMap(measurements);
        } finally {
            setLoadingMapPoints(false);
        }
    }, [dateRange, measurements]);

    const fetchPatterns = useCallback(async (areaId: number) => {
        setLoadingPatterns(true);
        try {
            const res = await axios.get(ENDPOINTS.AREA_PATTERNS(areaId), {
                params: { limit: 200 },
                withCredentials: true,
            });
            const data: PatternItem[] = res.data?.data || res.data || [];
            setPatterns(data);
        } catch (error) {
            console.error('Failed to load patterns', error);
            setPatterns([]);
        } finally {
            setLoadingPatterns(false);
        }
    }, []);

    const fetchPatternPoints = useCallback(async (patternIds: number[]) => {
        if (patternIds.length === 0) {
            setPatternPointsByPattern({});
            return;
        }

        try {
            const pointsMap: Record<number, PatternPointItem[]> = {};
            
            // Fetch pattern points for each pattern
            await Promise.all(
                patternIds.map(async (patternId) => {
                    try {
                        const res = await axios.get(ENDPOINTS.PATTERN_POINTS(patternId), {
                            withCredentials: true,
                        });
                        const points: PatternPointItem[] = res.data?.data || res.data || [];
                        pointsMap[patternId] = points;
                    } catch (error) {
                        console.error(`Failed to load points for pattern ${patternId}`, error);
                        pointsMap[patternId] = [];
                    }
                })
            );
            
            setPatternPointsByPattern(pointsMap);
            console.log('[Measurements] Pattern points loaded for patterns:', patternIds);
        } catch (error) {
            console.error('Failed to load pattern points', error);
            setPatternPointsByPattern({});
        }
    }, []);


    // State for area counts (from server-side aggregation)
    const [areaCountsMap, setAreaCountsMap] = useState<Map<number | 'no-area', MeasurementCounts>>(new Map());
    
    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    useEffect(() => {
        const loadData = async () => {
            setLoadingAreaCounts(true);
            // Fetch area counts using optimized endpoint - inline to avoid dependency issues
            try {
                const params: Record<string, any> = {};
                
                if (dateRange && dateRange[0] && dateRange[1]) {
                    params.from_date = dateRange[0].startOf('day').toISOString();
                    params.to_date = dateRange[1].endOf('day').toISOString();
                }
                
                const res = await axios.get(ENDPOINTS.MEASUREMENTS_AREA_COUNTS, {
                    params,
                    withCredentials: true,
                });
                
                const counts: Array<{
                    areaId: number | null;
                    total: number;
                    assignedToPoint: number;
                    inPatternNoPoint: number;
                    noPattern: number;
                }> = res.data?.data || [];
                
                console.log('[Measurements] Area counts loaded:', counts.length);
                
                const map = new Map<number | 'no-area', MeasurementCounts>();
                counts.forEach(c => {
                    const key = (c.areaId ?? 'no-area') as number | 'no-area';
                    map.set(key, {
                        total: c.total,
                        assignedToPoint: c.assignedToPoint,
                        inPatternNoPoint: c.inPatternNoPoint,
                        noPattern: c.noPattern
                    });
                });
                setAreaCountsMap(map);
            } catch (error) {
                console.error('Failed to load area counts', error);
                setAreaCountsMap(new Map());
            } finally {
                setLoadingAreaCounts(false);
            }
        };
        loadData();
    }, [dateRange]); // Only depends on dateRange, not on any functions

    useEffect(() => {
        if (selectedAreaId && selectedAreaId !== 'no-area') {
            fetchPatterns(selectedAreaId);
        } else {
            setPatterns([]);
        }
        
        // Clear selected measurement and fetch measurements for the newly selected area
        if (selectedAreaId) {
            setSelectedMeasurement(null); // Clear details card during transition
            setCurrentPage(1); // Reset to first page
            fetchMeasurements(1, pageSize, selectedAreaId);
            fetchMeasurementsForMap(selectedAreaId); // Fetch map markers separately (higher limit)
        }
    }, [selectedAreaId, pageSize, dateRange]); // Re-fetch when date range changes

    // Fetch pattern points when patterns are loaded
    useEffect(() => {
        if (patterns.length > 0) {
            const patternIds = patterns.map(p => p.id);
            fetchPatternPoints(patternIds);
        } else {
            setPatternPointsByPattern({});
        }
    }, [patterns, fetchPatternPoints]);


    const noAreaCounts = useMemo(() => areaCountsMap.get('no-area') || null, [areaCountsMap]);

    // Use server-side counts from area counts endpoint
    const areasWithCounts: AreaRow[] = useMemo(() => {
        return areas.map((area) => ({
            ...area,
            counts: areaCountsMap.get(area.id) || {
                total: 0,
                assignedToPoint: 0,
                inPatternNoPoint: 0,
                noPattern: 0,
            },
        }));
    }, [areas, areaCountsMap]);

    useEffect(() => {
        const areasWithData = areasWithCounts.filter((area) => (area.counts?.total || 0) > 0);
        
        // If no areas have measurements, clear selection to show Empty state
        if (areasWithData.length === 0) {
            setSelectedAreaId(null);
            // Only update if not already empty to avoid re-render loops
            setMeasurements((prev) => prev.length === 0 ? prev : []);
            setMeasurementsForMap((prev) => prev.length === 0 ? prev : []);
            setLoadingMeasurements(false);
            setLoadingMapPoints(false);
        } 
        // If no area is selected and we have areas, select the first one with measurements
        else if (!selectedAreaId) {
            setSelectedAreaId(areasWithData[0].id);
        }
    }, [areasWithCounts, selectedAreaId]);

    const selectedArea = useMemo(
        () => areas.find((a) => a.id === selectedAreaId) || null,
        [areas, selectedAreaId],
    );

    // Pass only the selected area to map for proper zoom focus
    const areasForMap = useMemo(
        () => {
            if (!selectedArea || selectedAreaId === 'no-area') return [];
            return [{
                id: selectedArea.id as number,
                geom: selectedArea.geom,
                name: selectedArea.name,
                areaType: selectedArea.areaType || null,
                hierarchyDepth: 0,
            }];
        },
        [selectedArea, selectedAreaId],
    );

    // Load area type template image for selected area (cache by type ID)
    useEffect(() => {
        const loadTemplateForArea = async () => {
            if (!selectedArea?.areaType?.id) return;
            const typeId = selectedArea.areaType.id;
            if (checkedAreaTypes.has(typeId)) return;
            setCheckedAreaTypes((prev) => new Set(prev).add(typeId));
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${typeId}`, {
                    withCredentials: true,
                });
                const hasTemplate = res.data?.data?.templateImage || res.data?.templateImage;
                if (hasTemplate) {
                    const imageUrl = `${ENDPOINTS.AREA_TYPES_V2}/${typeId}/template-image?t=${Date.now()}`;
                    setAreaTypeTemplates((prev) => {
                        const next = new Map(prev);
                        next.set(typeId, imageUrl);
                        return next;
                    });
                }
            } catch (err) {
                console.warn('Failed to load area type template', err);
            }
        };
        loadTemplateForArea();
    }, [selectedArea, checkedAreaTypes]);

    // No need for client-side filtering - API now filters by area_id or unassigned
    const areaMeasurements = measurements;

    const patternGroups = useMemo(() => {
        const byPattern = new Map<number, { pattern: PatternItem | null; assigned: Record<number, MeasurementItem[]>; unassigned: MeasurementItem[] }>();
        areaMeasurements.forEach((m) => {
            if (!m.patternId) return;
            const record =
                byPattern.get(m.patternId) ||
                { pattern: m.pattern || null, assigned: {}, unassigned: [] };  // Use pattern from measurement object
            if (m.patternPointId) {
                if (!record.assigned[m.patternPointId]) record.assigned[m.patternPointId] = [];
                record.assigned[m.patternPointId].push(m);
            } else {
                record.unassigned.push(m);
            }
            byPattern.set(m.patternId, record);
        });
        return Array.from(byPattern.entries()).map(([id, data]) => ({ patternId: id, ...data }));
    }, [areaMeasurements]);

    const noPatternMeasurements = useMemo(
        () => areaMeasurements.filter((m) => !m.patternId),
        [areaMeasurements],
    );

    const getMeasurementStatus = useCallback((measurement: MeasurementItem): MeasurementStatusKey => {
        if (!measurement.areaId) {
            return 'no-area';
        }
        if (measurement.patternPointId) {
            return 'assigned';
        }
        if (measurement.patternId) {
            return 'in-pattern-no-point';
        }
        return 'no-pattern';
    }, []);

    const measurementTableRows = useMemo<MeasurementTableRow[]>(() => {
        const rows: MeasurementTableRow[] = [];

        patternGroups.forEach((group) => {
            const patternPoints = patternPointsByPattern[group.patternId] || [];
            const orderedAssignedGroups = Object.entries(group.assigned).sort(([leftId], [rightId]) => {
                const leftPoint = patternPoints.find((point) => point.id === Number(leftId));
                const rightPoint = patternPoints.find((point) => point.id === Number(rightId));
                return (leftPoint?.pointIndex ?? Number(leftId)) - (rightPoint?.pointIndex ?? Number(rightId));
            });

            orderedAssignedGroups.forEach(([, items]) => {
                items.forEach((measurement) => {
                    rows.push({
                        ...measurement,
                        statusKey: getMeasurementStatus(measurement),
                    });
                });
            });

            group.unassigned.forEach((measurement) => {
                rows.push({
                    ...measurement,
                    statusKey: getMeasurementStatus(measurement),
                });
            });
        });

        noPatternMeasurements.forEach((measurement) => {
            rows.push({
                ...measurement,
                statusKey: getMeasurementStatus(measurement),
            });
        });

        return rows;
    }, [getMeasurementStatus, noPatternMeasurements, patternGroups, patternPointsByPattern]);

    const measurementStatusCounts = useMemo(() => {
        return measurementTableRows.reduce<Record<MeasurementStatusKey, number>>(
            (counts, measurement) => {
                counts[measurement.statusKey] += 1;
                return counts;
            },
            {
                assigned: 0,
                'in-pattern-no-point': 0,
                'no-pattern': 0,
                'no-area': 0,
            },
        );
    }, [measurementTableRows]);

    const markerPoints = useMemo(() => {
        // Use map-specific data (up to 2000) instead of paginated list data
        return measurementsForMap
            .filter((m) => m.geom?.type === 'Point' && Array.isArray(m.geom.coordinates))
            .map((m) => ({
                id: m.id,
                lat: m.geom!.coordinates[1],
                lng: m.geom!.coordinates[0],
                label: m.patternPoint?.label || m.measurementCode,
                selected: selectedMeasurement?.id === m.id,
            }));
    }, [measurementsForMap, selectedMeasurement]);

    const mapInitialView = useMemo(() => {
        const point = selectedMeasurement?.geom;
        if (point?.type === 'Point' && Array.isArray(point.coordinates)) {
            return {
                longitude: point.coordinates[0],
                latitude: point.coordinates[1],
                zoom: 17,
            };
        }
        return undefined;
    }, [selectedMeasurement]);

    const patternPointsForMap = useMemo(() => {
        const list: { id?: string | number; lat: number; lng: number; label?: string }[] = [];
        Object.values(patternPointsByPattern).forEach((points) => {
            points.forEach((p) => {
                if (p.gpsLat !== null && p.gpsLat !== undefined && p.gpsLon !== null && p.gpsLon !== undefined) {
                    list.push({
                        id: p.id,
                        lat: p.gpsLat,
                        lng: p.gpsLon,
                        label: p.label || `#${p.pointIndex + 1}`,
                    });
                }
            });
        });
        return list;
    }, [patternPointsByPattern]);

    const handleRowClick = (record: AreaRow) => {
        setSelectedAreaId(record.id);
    };

    const columns: ColumnsType<AreaRow> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <span>{text}</span>
                    {record.isSynthetic && <Tag color="orange">Unassigned</Tag>}
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: ['areaType', 'name'],
            key: 'areaType',
            render: (_: any, record: AreaRow) => record.areaType?.name || '—',
        },
        {
            title: 'In a pattern',
            key: 'inPattern',
            align: 'center',
            render: (_: any, record: AreaRow) => {
                const counts = record.counts;
                if (!counts) return '—';
                return counts.assignedToPoint + counts.inPatternNoPoint;
            },
        },
        {
            title: 'Total',
            key: 'total',
            align: 'center',
            render: (_: any, record: AreaRow) => {
                const counts = record.counts;
                if (!counts) return '—';
                return counts.total;
            },
        },
    ];

    const unassignedColumns: ColumnsType<AreaRow> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <span>{text}</span>
                    {record.isSynthetic && <Tag color="orange">Unassigned</Tag>}
                </Space>
            ),
        },
        {
            title: 'Total',
            key: 'total',
            align: 'center',
            render: (_: any, record: AreaRow) => {
                const counts = record.counts;
                if (!counts) return '—';
                return counts.total;
            },
        },
    ];

    const loadFilesForMeasurement = useCallback(async (measurement: MeasurementItem) => {
        setFilesLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.MEASUREMENT_FILES_BATCH_URLS(measurement.id), {
                withCredentials: true,
            });
            const files: MeasurementFile[] = res.data?.data?.files || [];
            setSelectedFiles(filterProcessed(files));
        } catch (error) {
            console.warn('Unable to load signed URLs, falling back to raw file list', error);
            setSelectedFiles(filterProcessed(measurement.files || []));
        } finally {
            setFilesLoading(false);
        }
    }, [filterProcessed]);

    const handleSelectMeasurement = useCallback((measurement: MeasurementItem) => {
        setSelectedMeasurement({
            ...measurement,
            values: filterProcessed(measurement.values),
            files: filterProcessed(measurement.files),
            texts: filterProcessed(measurement.texts),
        });
        loadFilesForMeasurement(measurement);
    }, [filterProcessed, loadFilesForMeasurement]);

    const measurementById = useMemo(() => {
        const map = new Map<number, MeasurementItem>();
        [...measurementsForMap, ...measurements].forEach((item) => {
            if (typeof item.id === 'number') {
                map.set(item.id, item);
            }
        });
        return map;
    }, [measurementsForMap, measurements]);

    const handleMapMeasurementClick = useCallback(async (measurementId: string | number) => {
        const numericId = typeof measurementId === 'string' ? Number(measurementId) : measurementId;
        const existing = measurementById.get(numericId);
        if (existing) {
            handleSelectMeasurement(existing);
            return;
        }
        try {
            const res = await axios.get(ENDPOINTS.MEASUREMENT_DETAIL(measurementId), {
                withCredentials: true,
            });
            const payload = res.data?.data || res.data;
            if (payload) {
                handleSelectMeasurement({
                    ...payload,
                    values: filterProcessed(payload.values),
                    files: filterProcessed(payload.files),
                    texts: filterProcessed(payload.texts),
                });
                return;
            }
        } catch (error) {
            console.error('Failed to load measurement details', error);
        }
        message.error('Could not load measurement details');
    }, [measurementById, handleSelectMeasurement]);

    const handleUnassignArea = useCallback(async (measurement: MeasurementItem) => {
        if (!measurement.areaId) {
            return;
        }
        try {
            const res = await axios.patch(
                ENDPOINTS.MEASUREMENTS_BULK_UPDATE_AREA,
                {
                    measurementIds: [measurement.id],
                    areaId: null,
                },
                { withCredentials: true }
            );
            message.success(res.data?.message || 'Measurement unassigned from area');
            if (selectedMeasurement?.id === measurement.id) {
                setSelectedMeasurement((prev) => prev ? { ...prev, areaId: null, area: undefined } : prev);
            }
            await refreshAreaCounts();
            await fetchMeasurements(currentPage, pageSize, selectedAreaId);
            await fetchMeasurementsForMap(selectedAreaId);
        } catch (error: any) {
            console.error('Failed to unassign measurement', error);
            message.error(error.response?.data?.message || 'Failed to unassign measurement');
        }
    }, [
        currentPage,
        fetchMeasurements,
        fetchMeasurementsForMap,
        pageSize,
        refreshAreaCounts,
        selectedAreaId,
        selectedMeasurement,
    ]);

    const handleUnassignPatternPoint = useCallback(async (measurement: MeasurementItem) => {
        if (!measurement.patternId) {
            message.warning('Measurement has no pattern assigned');
            return;
        }
        try {
            const res = await axios.patch(
                ENDPOINTS.MEASUREMENTS_BULK_UPDATE_PATTERN,
                {
                    measurementIds: [measurement.id],
                    patternId: measurement.patternId,
                    patternPointId: null,
                },
                { withCredentials: true }
            );
            message.success(res.data?.message || 'Pattern point unassigned');
            if (selectedMeasurement?.id === measurement.id) {
                setSelectedMeasurement((prev) =>
                    prev ? { ...prev, patternPointId: null, patternPoint: null } : prev
                );
            }
            await fetchMeasurements(currentPage, pageSize, selectedAreaId);
            await fetchMeasurementsForMap(selectedAreaId);
        } catch (error: any) {
            console.error('Failed to unassign pattern point', error);
            message.error(error.response?.data?.message || 'Failed to unassign pattern point');
        }
    }, [
        currentPage,
        fetchMeasurements,
        fetchMeasurementsForMap,
        pageSize,
        selectedAreaId,
        selectedMeasurement,
    ]);

    const handleUnassignPattern = useCallback(async (measurement: MeasurementItem) => {
        if (!measurement.patternId) {
            message.warning('Measurement has no pattern assigned');
            return;
        }
        try {
            const res = await axios.patch(
                ENDPOINTS.MEASUREMENTS_BULK_UPDATE_PATTERN,
                {
                    measurementIds: [measurement.id],
                    patternId: null,
                    patternPointId: null,
                },
                { withCredentials: true }
            );
            message.success(res.data?.message || 'Pattern unassigned');
            if (selectedMeasurement?.id === measurement.id) {
                setSelectedMeasurement((prev) =>
                    prev ? { ...prev, patternId: null, pattern: null, patternPointId: null, patternPoint: null } : prev
                );
            }
            await fetchMeasurements(currentPage, pageSize, selectedAreaId);
            await fetchMeasurementsForMap(selectedAreaId);
        } catch (error: any) {
            console.error('Failed to unassign pattern', error);
            message.error(error.response?.data?.message || 'Failed to unassign pattern');
        }
    }, [
        currentPage,
        fetchMeasurements,
        fetchMeasurementsForMap,
        pageSize,
        selectedAreaId,
        selectedMeasurement,
    ]);

    const handleUnassignAll = useCallback(async (measurement: MeasurementItem) => {
        const hasAreaAssignment = Boolean(measurement.areaId);
        const hasPatternAssignment = Boolean(measurement.patternId || measurement.patternPointId);
        if (!hasAreaAssignment && !hasPatternAssignment) {
            return;
        }

        try {
            if (hasPatternAssignment) {
                await axios.patch(
                    ENDPOINTS.MEASUREMENTS_BULK_UPDATE_PATTERN,
                    {
                        measurementIds: [measurement.id],
                        patternId: null,
                        patternPointId: null,
                    },
                    { withCredentials: true }
                );
            }

            if (hasAreaAssignment) {
                await axios.patch(
                    ENDPOINTS.MEASUREMENTS_BULK_UPDATE_AREA,
                    {
                        measurementIds: [measurement.id],
                        areaId: null,
                    },
                    { withCredentials: true }
                );
            }

            message.success('Measurement fully unassigned');
            if (selectedMeasurement?.id === measurement.id) {
                setSelectedMeasurement((prev) =>
                    prev
                        ? {
                              ...prev,
                              areaId: null,
                              area: undefined,
                              patternId: null,
                              pattern: null,
                              patternPointId: null,
                              patternPoint: null,
                          }
                        : prev
                );
            }

            await refreshAreaCounts();
            await fetchMeasurements(currentPage, pageSize, selectedAreaId);
            await fetchMeasurementsForMap(selectedAreaId);
        } catch (error: any) {
            console.error('Failed to unassign all measurement assignments', error);
            message.error(error.response?.data?.message || 'Failed to unassign all');
        }
    }, [
        currentPage,
        fetchMeasurements,
        fetchMeasurementsForMap,
        pageSize,
        refreshAreaCounts,
        selectedAreaId,
        selectedMeasurement,
    ]);

    const showUnassignConfirm = useCallback((measurement: MeasurementItem, action: 'area' | 'point' | 'pattern' | 'all') => {
        const config = {
            area: {
                title: 'Remove area assignment?',
                content: 'This measurement will no longer belong to the area.',
                onOk: () => handleUnassignArea(measurement),
            },
            point: {
                title: 'Remove pattern point?',
                content: 'This measurement will no longer be assigned to the pattern point.',
                onOk: () => handleUnassignPatternPoint(measurement),
            },
            pattern: {
                title: 'Remove pattern?',
                content: 'This measurement will no longer be assigned to the pattern.',
                onOk: () => handleUnassignPattern(measurement),
            },
            all: {
                title: 'Unassign everything?',
                content: 'This measurement will be unassigned from area, pattern, and point.',
                onOk: () => handleUnassignAll(measurement),
            },
        }[action];

        if (!config) return;

        Modal.confirm({
            title: config.title,
            content: config.content,
            okText: 'Unassign',
            cancelText: 'Cancel',
            okButtonProps: { danger: true },
            onOk: config.onOk,
        });
    }, [handleUnassignAll, handleUnassignArea, handleUnassignPattern, handleUnassignPatternPoint]);

    const measurementColumns: ColumnsType<MeasurementTableRow> = [
        {
            title: 'Measurement',
            dataIndex: 'measurementCode',
            key: 'measurementCode',
            width: 240,
            render: (_value, item) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{item.measurementCode}</Text>
                    {item.device?.label ? (
                        <Text type="secondary" className="measurement-table-cell-secondary">
                            {item.device.label}
                        </Text>
                    ) : null}
                </Space>
            ),
        },
        {
            title: 'Taken',
            key: 'taken',
            width: 190,
            render: (_value, item) => (
                item.measuredAt ? (
                    <Text>{formatMeasurementTimestamp(item.measuredAt, item)}</Text>
                ) : (
                    <Tooltip title="Using creation date - measured date not available">
                        <Text type="secondary">
                            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />
                            {formatUtcTimestamp(item.createdAt)}
                        </Text>
                    </Tooltip>
                )
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 360,
            render: (_value, item) => {
                const hasAnyAssignment = Boolean(item.areaId || item.patternId || item.patternPointId);
                const unassignItems = [
                    hasAnyAssignment ? { key: 'all', label: 'Unassign all' } : null,
                    item.areaId ? { key: 'area', label: 'Unassign area' } : null,
                    item.patternPointId ? { key: 'point', label: 'Unassign point' } : null,
                    item.patternId ? { key: 'pattern', label: 'Unassign pattern' } : null,
                ].filter(Boolean) as Array<{ key: 'area' | 'point' | 'pattern' | 'all'; label: string }>;

                const unassignMenu: MenuProps = {
                    items: unassignItems,
                    onClick: (info) => {
                        info.domEvent?.stopPropagation();
                        showUnassignConfirm(item, info.key as 'area' | 'point' | 'pattern' | 'all');
                    },
                };

                return (
                    <Space size={8} wrap className="measurement-table-actions">
                        <Button
                            key="view"
                            className="measurement-action-view-details"
                            size="small"
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/measurements/${item.id}`);
                            }}
                        >
                            View Details
                        </Button>
                        <Button
                            key="edit-gps"
                            className="measurement-action-edit-gps"
                            size="small"
                            icon={<EnvironmentOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/measurements/${item.id}/edit-location`);
                            }}
                        >
                            {item.geom ? 'Edit' : 'Set'} GPS
                        </Button>
                        {!item.areaId ? (
                            <Button
                                key="assign-area"
                                className="measurement-action-assign-area"
                                size="small"
                                type="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/measurements/assign-areas?measurementId=${item.id}`);
                                }}
                            >
                                Assign to area
                            </Button>
                        ) : !item.patternId ? (
                            <Button
                                key="assign-pattern"
                                className="measurement-action-assign-pattern"
                                size="small"
                                type="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/measurements/assign-patterns?measurementId=${item.id}`);
                                }}
                            >
                                Assign to pattern
                            </Button>
                        ) : (
                            <Button
                                key="edit-pattern"
                                className="measurement-action-edit-pattern"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/measurements/assign-patterns?measurementId=${item.id}`);
                                }}
                            >
                                Edit pattern
                            </Button>
                        )}
                        {unassignItems.length ? (
                            <Dropdown key="unassign-menu" menu={unassignMenu} trigger={['click']}>
                                <Button
                                    className="measurement-action-unassign"
                                    size="small"
                                    danger
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Unassign <DownOutlined />
                                </Button>
                            </Dropdown>
                        ) : null}
                    </Space>
                );
            },
        },
    ];

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
        }
        fetchMeasurements(page, size || pageSize);
    };

    return (
        <div className="measurements-page">
            <Row className="measurements-header" justify="space-between" align="middle">
                <Title level={2} style={{ margin: 0 }}>Measurement setup</Title>
                <Button
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={() => navigate('/measurements/import')}
                >
                    Import Measurements
                </Button>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={14}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card size="small" className="measurements-date-filter-card" style={{ background: '#f5f5f5' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Date range filter</Text>
                                <Space.Compact style={{ width: '100%' }}>
                                    <RangePicker
                                        className="measurements-date-range-picker"
                                        value={dateRange}
                                        onChange={async (range) => {
                                            if (range) {
                                                // Clear current data and show loading
                                                setMeasurements([]);
                                                setSelectedMeasurement(null); // Clear details card during date change
                                                setLoadingMeasurements(true);
                                                
                                                // Update date range
                                                const newRange = range as [Dayjs, Dayjs];
                                                setDateRange(newRange);
                                                
                                                // Persist to localStorage for other views
                                                localStorage.setItem('measurementDateRange', JSON.stringify({
                                                    from: newRange[0].format('YYYY-MM-DD'),
                                                    to: newRange[1].format('YYYY-MM-DD')
                                                }));
                                                
                                                // Reset to page 1
                                                setCurrentPage(1);
                                            }
                                        }}
                                        allowClear={false}
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                    />
                                    {latestUploadsHidden && (
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setDateRange([dateRange[0], dayjs()]);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            Show latest uploads
                                        </Button>
                                    )}
                                            <Tooltip title="Reload measurements">
                                                <Button 
                                                    className="measurements-reload-button"
                                                    icon={<ReloadOutlined />} 
                                                    loading={loadingMeasurements}
                                                    onClick={() => {
                                                const loadData = async () => {
                                                    await refreshAreaCounts();
                                                    await fetchMeasurements(currentPage, pageSize);
                                                };
                                                loadData();
                                                    }} 
                                                />
                                            </Tooltip>
                                </Space.Compact>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Filters measurements by measured date using the selected date range
                                </Text>
                            </Space>
                        </Card>
                        {noAreaCounts && noAreaCounts.total > 0 && (
                            <Card
                                title="Measurements found without area assigned"
                                className="measurements-table-card measurements-unassigned-card"
                            >
                                {loadingAreaCounts ? (
                                    <Spin />
                                ) : (
                                    <Table
                                        size="small"
                                        rowKey={(record) => String(record.id)}
                                        columns={unassignedColumns}
                                        dataSource={[
                                            {
                                                id: 'no-area',
                                                name: 'No area assigned',
                                                areaType: null,
                                                isSynthetic: true,
                                                counts: noAreaCounts,
                                            },
                                        ]}
                                        pagination={false}
                                        className="measurements-table"
                                        rowClassName={(record) =>
                                            record.id === selectedAreaId ? 'area-row-selected' : ''
                                        }
                                        onRow={(record) => ({
                                            onClick: () => handleRowClick(record),
                                        })}
                                    />
                                )}
                            </Card>
                        )}
                        <Card title="Measurements found in the following areas" className="measurements-table-card measurements-areas-table-card">
                            {loadingAreas || loadingAreaCounts ? (
                                <Spin />
                            ) : areasWithCounts.filter((area) => (area.counts?.total || 0) > 0).length === 0 ? (
                                <Empty 
                                    description={
                                        <Space direction="vertical" size={4}>
                                            <Text strong>No measurements found in selected date range</Text>
                                            <Text type="secondary">
                                                {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Try expanding the date range to see measurements
                                            </Text>
                                        </Space>
                                    }
                                />
                            ) : (
                                <Table
                                    size="small"
                                    rowKey={(record) => String(record.id)}
                                    columns={columns}
                                    dataSource={areasWithCounts.filter((area) => (area.counts?.total || 0) > 0)}
                                    pagination={false}
                                    className="measurements-table"
                                    rowClassName={(record) =>
                                        record.id === selectedAreaId ? 'area-row-selected' : ''
                                    }
                                    onRow={(record) => ({
                                        onClick: () => handleRowClick(record),
                                    })}
                                />
                            )}
                        </Card>

                        <Card
                            title="Measurements"
                            className="measurements-list-card"
                            extra={
                                <Space size={[8, 8]} wrap className="measurements-status-summary">
                                    {measurementStatusCounts.assigned > 0 && (
                                        <Tag color="green">Assigned {measurementStatusCounts.assigned}</Tag>
                                    )}
                                    {measurementStatusCounts['in-pattern-no-point'] > 0 && (
                                        <Tag color="gold">
                                            In pattern, no point {measurementStatusCounts['in-pattern-no-point']}
                                        </Tag>
                                    )}
                                    {measurementStatusCounts['no-pattern'] > 0 && (
                                        <Tag>No pattern {measurementStatusCounts['no-pattern']}</Tag>
                                    )}
                                    {measurementStatusCounts['no-area'] > 0 && (
                                        <Tag color="orange">No area {measurementStatusCounts['no-area']}</Tag>
                                    )}
                                </Space>
                            }
                        >
                            {loadingMeasurements ? (
                                <Spin />
                            ) : areaMeasurements.length === 0 ? (
                                <Empty description="No measurements in this selection" />
                            ) : (
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Text type="secondary" className="measurements-table-hint">
                                        A table view keeps the measurement selection, map, and actions easier to scan.
                                    </Text>
                                    <Table
                                        size="small"
                                        rowKey={(record) => String(record.id)}
                                        columns={measurementColumns}
                                        dataSource={measurementTableRows}
                                        pagination={false}
                                        scroll={{ x: 820 }}
                                        className="measurements-items-table"
                                        rowClassName={(record) =>
                                            selectedMeasurement?.id === record.id ? 'measurement-selected' : ''
                                        }
                                        onRow={(record) => ({
                                            onClick: () => handleSelectMeasurement(record),
                                        })}
                                    />
                                    
                                    <Divider style={{ margin: '16px 0' }} />
                                    <Pagination
                                        className="measurements-pagination"
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={totalMeasurements}
                                        onChange={handlePageChange}
                                        onShowSizeChange={handlePageChange}
                                        showSizeChanger
                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} measurements`}
                                        pageSizeOptions={['10', '20']}
                                        size="small"
                                        style={{ textAlign: 'center' }}
                                    />
                                </Space>
                            )}
                        </Card>
                    </Space>
                </Col>

                <Col xs={24} xl={10}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card
                            title={
                                selectedAreaId === 'no-area'
                                    ? 'Points not assigned to an area'
                                    : selectedArea?.name
                                    ? `Area map · ${selectedArea.name}`
                                    : 'Area map'
                            }
                            className="measurements-map-card"
                        >
                            <div className="measurements-map-wrapper">
                                {(loadingAreas || loadingMapPoints) && (
                                    <div className="measurements-map-loading">
                                        <Spin tip="Loading points..." />
                                    </div>
                                )}
                                {!loadingAreas && (
                                    <AreaMap
                                        mapKey={`${selectedAreaId || 'no-area'}-${selectedMeasurement?.id || 'none'}`}
                                        areaGeometry={selectedArea?.geom}
                                        areaName={selectedArea?.name || 'No area'}
                                        measurements={markerPoints}
                                        points={patternPointsForMap}
                                        allAreas={areasForMap}
                                        areaType={selectedArea?.areaType || null}
                                        initialViewState={mapInitialView}
                                        disableAutoFit={!!selectedMeasurement || loadingMapPoints}
                                        areaTypeTemplates={areaTypeTemplates}
                                        onMeasurementClick={handleMapMeasurementClick}
                                    />
                                )}
                            </div>
                        </Card>

                        <Card
                            title={
                                <Space>
                                    <ThunderboltOutlined />
                                    <span>Measurement details</span>
                                </Space>
                            }
                            className="measurements-details-card"
                        >
                            {selectedMeasurement ? (
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <div>
                                        <Text strong>{selectedMeasurement.measurementCode}</Text>
                                        <div className="measurement-meta">
                                            <Tag>
                                                Uploaded: {formatUtcTimestamp(selectedMeasurement.createdAt)}
                                            </Tag>
                                            <Tag color="blue">
                                                Measured: {selectedMeasurement.measuredAt
                                                    ? formatMeasurementTimestamp(selectedMeasurement.measuredAt, selectedMeasurement)
                                                    : '—'}
                                            </Tag>
                                            {selectedMeasurement.device?.label && (
                                                <Tag icon={<ThunderboltOutlined />}>{selectedMeasurement.device.label}</Tag>
                                            )}
                                            {selectedMeasurement.pattern?.name && <Tag color="blue">{selectedMeasurement.pattern.name}</Tag>}
                                            {selectedMeasurement.patternPoint?.label && (
                                                <Tag color="green">{selectedMeasurement.patternPoint.label}</Tag>
                                            )}
                                            {!selectedMeasurement.patternId && <Tag color="gray">No pattern</Tag>}
                                        </div>
                                    </div>

                                    <div>
                                        <Text strong>Values</Text>
                                        {selectedMeasurement.values && selectedMeasurement.values.length > 0 ? (
                                            <List
                                                size="small"
                                                dataSource={selectedMeasurement.values}
                                                renderItem={(v) => (
                                                    <List.Item>
                                                        <Space split={<Divider type="vertical" />}>
                                                            <Text>{v.valueType}</Text>
                                                            <Text strong>{v.textValue ?? v.numValue ?? '—'}</Text>
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        ) : (
                                            <Text type="secondary">No values</Text>
                                        )}
                                    </div>

                                    <div>
                                        <Text strong>Notes</Text>
                                        {selectedMeasurement.texts && selectedMeasurement.texts.length > 0 ? (
                                            <List
                                                size="small"
                                                dataSource={selectedMeasurement.texts}
                                                renderItem={(t) => (
                                                    <List.Item>
                                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                            <Space wrap>
                                                                <Tag color="blue">{t.textType || 'comment'}</Tag>
                                                                {t.createdAt && (
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                                        {dayjs(t.createdAt).format('DD/MM/YYYY HH:mm')}
                                                                    </Text>
                                                                )}
                                                            </Space>
                                                            <Text>{t.text}</Text>
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        ) : (
                                            <Text type="secondary">No notes</Text>
                                        )}
                                    </div>

                                    <div>
                                        <Text strong>Images</Text>
                                        {filesLoading ? (
                                            <Spin />
                                        ) : selectedFiles.filter((f) => (f.mimeType || '').startsWith('image/')).length > 0 ? (
                                            <Space size="middle" wrap>
                                                {selectedFiles
                                                    .filter((f) => (f.mimeType || '').startsWith('image/'))
                                                    .map((file) => (
                                                        <div key={file.id} className="measurement-image-thumb">
                                                            <img src={file.signedUrl || ''} alt={file.fileName} />
                                                            <div className="measurement-image-caption">
                                                                <FileImageOutlined /> {file.fileName}
                                                            </div>
                                                        </div>
                                                    ))}
                                            </Space>
                                        ) : (
                                            <Text type="secondary">No images</Text>
                                        )}
                                    </div>

                                    <div>
                                        <Text strong>Location</Text>
                                        {selectedMeasurement.geom?.type === 'Point' && Array.isArray(selectedMeasurement.geom.coordinates) ? (
                                            <div style={{ marginTop: 8 }}>
                                                <Space size="small">
                                                    <Tag icon={<EnvironmentOutlined />}>
                                                        {selectedMeasurement.geom.coordinates[1].toFixed(6)}, {selectedMeasurement.geom.coordinates[0].toFixed(6)}
                                                    </Tag>
                                                </Space>
                                            </div>
                                        ) : (
                                            <Text type="secondary">No GPS coordinates set</Text>
                                        )}
                                    </div>
                                </Space>
                            ) : (
                                <Text type="secondary">Select a measurement to see details.</Text>
                            )}
                        </Card>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default Measurements;
